import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import {
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const CHECKLIST_ITEMS = [
    'Enter/submit all scores',
    'Resolve playoffs for $$',
    'Confirm all challenges are submitted',
    'Award Individual Prizes',
    'Award Team Prizes',
    'Award Skins Prizes',
    'Award Challenge Prizes',
] as const;

const PANEL_WIDTH = 300;
const PANEL_TOP = 72;
const PANEL_RIGHT = 24;

type DragBounds = {
    left: number;
    top: number;
    right: number;
    bottom: number;
};

export const CommandCenterChecklistPopover = () => {
    const [open, setOpen] = useState(false);
    const [bounds, setBounds] = useState<DragBounds>({
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
    });
    const nodeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    const handleDragStart = (_event: unknown, data: { node: HTMLElement; x: number; y: number }) => {
        const { clientWidth, clientHeight } = document.documentElement;
        const targetRect = data.node.getBoundingClientRect();

        setBounds({
            left: -targetRect.left + data.x,
            top: -targetRect.top + data.y,
            right: clientWidth - (targetRect.right - data.x),
            bottom: clientHeight - (targetRect.bottom - data.y),
        });
    };

    return (
        <>
            <Tooltip title="Tournament wrap-up checklist">
                <IconButton
                    sx={{ width: 30, height: 30, borderRadius: 5 }}
                    onClick={() => setOpen((current) => !current)}
                    aria-label="Tournament wrap-up checklist"
                    aria-expanded={open}
                >
                    <ChecklistIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            {open &&
                createPortal(
                    <Draggable
                        nodeRef={nodeRef}
                        handle="#cc-checklist-drag-handle"
                        cancel="button, a, input, textarea, select"
                        bounds={bounds}
                        onStart={handleDragStart}
                    >
                        <Paper
                            ref={nodeRef}
                            elevation={8}
                            sx={{
                                position: 'fixed',
                                top: PANEL_TOP,
                                right: PANEL_RIGHT,
                                width: PANEL_WIDTH,
                                zIndex: (theme) => theme.zIndex.modal + 1,
                                p: 1.5,
                                cursor: 'default',
                            }}
                        >
                            <Stack spacing={1}>
                                <Stack
                                    id="cc-checklist-drag-handle"
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{
                                        cursor: 'grab',
                                        userSelect: 'none',
                                        '&:active': { cursor: 'grabbing' },
                                    }}
                                >
                                    <DragIndicatorIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary', flexShrink: 0 }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, flex: 1 }}
                                    >
                                        Wrap-up checklist
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setOpen(false)}
                                        aria-label="Close checklist"
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <List dense disablePadding>
                                    {CHECKLIST_ITEMS.map((item, index) => (
                                        <ListItem
                                            key={item}
                                            disableGutters
                                            sx={{ py: 0.25, alignItems: 'flex-start' }}
                                        >
                                            <ListItemText
                                                primary={`${index + 1}. ${item}`}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Stack>
                        </Paper>
                    </Draggable>,
                    document.body,
                )}
        </>
    );
};
